import React, { useCallback, useEffect, useRef, useState } from 'react'
import { batch } from '@preact/signals-react'
import { Box, Button, ButtonBase, Chip, Popover, TextField, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { getSubmit } from '@ui-seeker/utils'
import { useSearchParams } from 'react-router-dom'

import { AppNote, AppPillToggle } from '@components'
import { BACKEND_APIS } from '@constants'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { dsColorNew } from '@palette'
import {
	offices, officesOptions, reports, reportsInfo, reportsLoaded, repricingPass, repricingPassesInfo,
	repricingPassesLoaded, repricingPassesReload, userData,
} from '@signals'
import { resetCustomFilters } from 'Pages/AnalyzersV2/@signals'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import {
	KIND, executionKinds, groupPasses, openKind, passDisabledReason, passLabel, passOfficeOptions, passProgressLine,
	passStrategies, passWhen, passWhy,
} from './@services/executionOptions'

export const PASSES_URL = `${BACKEND_APIS.GENERAL_REPRICING_API}/analyzer/passes`

/**
 * "Execution" of DOR / LOR for an account with DS Repricing (CU-869fmm944, decision 1, UX Obs. 1, 3, 9): one field in
 * the header with the pills Reports · Repricing; picking in one kind replaces what was open. An account without DS
 * Repricing never mounts it — it keeps `ReportsAutocomplete`, unchanged.
 *
 * Reports come from the same `last_executions` call as the old field and fill `reportsInfo` / `officesOptions` the
 * same way; passes from `/api/repricing/analyzer/passes`.
 */
export default function ExecutionField({ directsite }) {
	const [searchParams] = useSearchParams()
	const anchor = useRef(null)
	const [open, setOpen] = useState(false)
	const kinds = executionKinds(userData.value)
	const [tab, setTab] = useState(() => openKind({ pass: repricingPass.value, report: reports.value, kinds }))
	const [query, setQuery] = useState('')
	const [passError, setPassError] = useState(false)

	const loadReports = useCallback(() => {
		reportsLoaded.value = false
		getSubmit(`${BACKEND_APIS.EXECUTION_INFO}/last_executions`, {
			idReportExecution: searchParams.get('idReportExecution') || '',
			idOfficePickup: searchParams.get('idOfficePickup') || '',
			...directsite != null && { getDirectSites: directsite },
		}).then(options => batch(() => {
			reportsInfo.value = Array.isArray(options) ? options : []
			const result = reportsInfo.value.find(item => item.value === reports.value)
			if (result) officesOptions.value = result.offices
			reportsLoaded.value = true
		})).catch(() => { reportsInfo.value = []; reportsLoaded.value = true })
	}, [searchParams])

	const loadPasses = useCallback(() => {
		repricingPassesLoaded.value = false
		setPassError(false)
		getSubmit(PASSES_URL).then(res => batch(() => {
			repricingPassesInfo.value = res?.items || []
			const current = repricingPassesInfo.value.find(p => p.execution_id === repricingPass.value)
			if (current) officesOptions.value = passOfficeOptions(current)
			repricingPassesLoaded.value = true
		})).catch(() => { setPassError(true); repricingPassesInfo.value = []; repricingPassesLoaded.value = true })
	}, [])

	useEffect(() => { loadReports() }, [loadReports])
	useEffect(() => { if (kinds.length > 1) loadPasses() }, [kinds.length, loadPasses, repricingPassesReload.value])

	const pickReport = option => batch(() => {
		resetCustomFilters()
		reports.value = option.value
		repricingPass.value = null
		officesOptions.value = option.offices || []
		setOpen(false)
	})

	const pickPass = pass => batch(() => {
		resetCustomFilters()
		repricingPass.value = pass.execution_id
		reports.value = null
		const options = passOfficeOptions(pass)
		officesOptions.value = options
		offices.value = options.length === 1 ? options[0].value : null
		setOpen(false)
	})

	const currentPass = repricingPassesInfo.value.find(p => p.execution_id === repricingPass.value)
	const currentReport = reportsInfo.value.find(r => r.value === reports.value)
	const value = repricingPass.value ? (currentPass ? passLabel(currentPass) : repricingPass.value)
		: (currentReport?.label || '')
	const kindTag = repricingPass.value ? 'Repricing' : (reports.value ? 'Report' : null)

	const show = () => {
		setTab(openKind({ pass: repricingPass.value, report: reports.value, kinds }))
		setQuery('')
		setOpen(true)
	}

	return <Box>
		<TextField
			ref={anchor} size="small" fullWidth label="Execution" value={value} placeholder="Please, select an execution"
			onClick={show}
			onKeyDown={e => { if (['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); show() } }}
			inputProps={{ readOnly: true, role: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': open,
				'aria-label': `Execution: ${value || 'none selected'}`, title: value, style: { cursor: 'pointer', textOverflow: 'ellipsis' } }}
			InputLabelProps={{ shrink: true }}
			InputProps={{
				readOnly: true,
				sx: { cursor: 'pointer' },
				startAdornment: kindTag ? <Chip size="small" label={kindTag} sx={{ ...toneChipSx(repricingPass.value ? 'info' : 'neutral'), mr: 0.75, height: 20, flexShrink: 0 }}/> : null,
				endAdornment: <IconSeeker icon="chevron-down" sx={{ fontSize: 11, color: dsColorNew.textSubtle, ml: 0.5 }}/>,
			}}
		/>

		<Popover open={open} anchorEl={anchor.current} onClose={() => setOpen(false)}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			PaperProps={{ sx: { mt: 0.5, p: 1.5, width: 480, maxWidth: 'calc(100vw - 32px)', borderRadius: '12px' } }}>
			{kinds.length > 1 && <AppPillToggle size="small" aria-label="Kind of execution" value={tab} onChange={setTab}
				options={kinds} sx={{ mb: 1.5 }}/>}
			<TextField size="small" fullWidth autoFocus value={query} onChange={e => setQuery(e.target.value)}
				placeholder={tab === KIND.REPRICING ? 'Search by strategy or date' : 'Search reports'}
				InputProps={{ startAdornment: <IconSeeker icon="magnifying-glass" sx={{ fontSize: 12, color: dsColorNew.textSubtle, mr: 1 }}/> }}
				sx={{ mb: 1 }}/>
			<Box sx={{ maxHeight: 360, overflowY: 'auto', mx: -0.5 }}>
				{tab === KIND.REPORTS
					? <ReportList query={query} onPick={pickReport}/>
					: <PassList query={query} error={passError} onRetry={loadPasses} onPick={pickPass}/>}
			</Box>
			{tab === KIND.REPRICING && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Your account's passes · last 30 days · newest first</Typography>
				<Button variant="text" size="small" href={DS_REPRICING_ROUTES.EXECUTIONS_PATH} sx={{ textTransform: 'none' }}>Executions</Button>
			</Box>}
		</Popover>
	</Box>
}

const optionSx = selected => ({
	display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left',
	px: 1, py: 0.75, borderRadius: '8px', fontFamily: 'inherit',
	...(selected && { backgroundColor: dsColorNew.pillActiveBg }),
	'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
	'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.appBlue}` },
	'&.Mui-disabled': { opacity: 0.55 },
})

function ReportList({ query, onPick }) {
	const q = query.trim().toLowerCase()
	const items = reportsInfo.value.filter(r => !q || String(r.label).toLowerCase().includes(q))
	if (!reportsLoaded.value) return <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, p: 1 }}>Loading…</Typography>
	if (!items.length) return <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, p: 1 }}>No reports in the last 30 days.</Typography>
	return items.map(r => <ButtonBase key={r.value} onClick={() => onPick(r)} sx={optionSx(r.value === reports.value)}>
		<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{r.label}</Typography>
	</ButtonBase>)
}

function PassList({ query, error, onRetry, onPick }) {
	if (error) return <AppNote severity="error" sx={{ m: 0.5 }}>
		The repricing passes could not be loaded. <Button size="small" variant="text" onClick={onRetry} sx={{ textTransform: 'none' }}>Try again</Button>
	</AppNote>
	if (!repricingPassesLoaded.value) return <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, p: 1 }}>Loading…</Typography>
	const groups = groupPasses(repricingPassesInfo.value, { query })
	if (!repricingPassesInfo.value.length) return <Box sx={{ p: 1 }}>
		<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textDark }}>No repricing passes in the last 30 days</Typography>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Passes appear here when your strategies run.</Typography>
		<Button variant="text" size="small" href={DS_REPRICING_ROUTES.EXECUTIONS_PATH} sx={{ textTransform: 'none', px: 0 }}>Go to Executions</Button>
	</Box>
	if (!groups.length) return <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, p: 1 }}>No pass matches the search.</Typography>
	return groups.map(group => <Box key={group.day} sx={{ mb: 0.5 }}>
		<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, px: 1, py: 0.5 }}>{group.day}</Typography>
		{group.items.map(pass => {
			const disabled = passDisabledReason(pass)
			return <ButtonBase key={pass.execution_id} disabled={!!disabled} onClick={() => onPick(pass)}
				title={disabled || undefined} sx={optionSx(pass.execution_id === repricingPass.value)}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textDark }}>{passWhen(pass)}</Typography>
					<Chip size="small" label={passWhy(pass)} sx={{ ...toneChipSx(pass.reason === 'force' ? 'info' : 'neutral'), height: 20 }}/>
				</Box>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
					{passStrategies(pass)} · {disabled || passProgressLine(pass)}
				</Typography>
			</ButtonBase>
		})}
	</Box>)
}
